import { useQuery } from '@tanstack/react-query';
import { useUserLogged } from './useUserLogged';
import { useAuthStore } from '@/state/authState';
import { fetchGitlabGroups } from '@/apis/fetchOrganization';

interface GitLabGroup {
  id: number;
  name: string;
  path: string;
  full_path: string;
  description: string | null;
  avatar_url: string | null;
  web_url: string;
}

export const useGitLabGroups = () => {
  const { user, organization, status } = useUserLogged();
  const { authUrl } = useAuthStore();

  // Only fetch groups when the provider is actually GitLab. Any other non-GitHub
  // provider (e.g. Bitbucket) would 401/403 against gitlab.com. Prefer the
  // organization's ``Source`` over the user's session ``provider`` since the
  // session value can be stale across provider switches.
  const providerRaw =
    (organization as { Source?: string } | null)?.Source ??
    user?.provider ??
    'github';
  const provider =
    typeof providerRaw === 'string' ? providerRaw.toLowerCase() : 'github';
  const isGitLab = provider === 'gitlab';

  const groupsQuery = useQuery({
    queryKey: ['gitlab-groups', user?.accessToken, authUrl],
    queryFn: async (): Promise<GitLabGroup[]> => {
      if (!user?.accessToken) {
        return [];
      }

      try {
        const groups = await fetchGitlabGroups(user.accessToken, authUrl);
        return groups || [];
      } catch (error) {
        console.error('Error fetching GitLab groups:', error);
        return [];
      }
    },
    enabled: Boolean(
      user?.accessToken && status === 'authenticated' && isGitLab
    ),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000,
  });

  return {
    groups: groupsQuery.data || [],
    isLoading: groupsQuery.isLoading,
    isError: groupsQuery.isError,
    error: groupsQuery.error,
    refetch: groupsQuery.refetch,
  };
};
